import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { UserRole } from "@KassaroAB/shared/types/enums/UserRole";
import { Input } from "../../components/Input";
import { useGetUserQuery } from "../../query/usersQuery";
import { useStoreSearchKeyQuery } from "../../query/storesQuery";
import { isAlgoliaSearchRole } from "../../utils/isAlgoliaSearchRole";
import { useStoreSearch } from "../../hooks/useStoreSearch";

const SEARCH_DEBOUNCE_MS = 300;
const MAX_DROPDOWN_HITS = 25;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const SearchInput = styled(Input)`
  width: 100%;
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const Dropdown = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + ${spacing.x1}px);
  left: 0;
  right: 0;
  max-height: min(280px, 50vh);
  overflow-y: auto;
  background-color: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1100;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  transform: ${({ $visible }) => ($visible ? "translateY(0)" : "translateY(-6px)")};
  transition:
    opacity 0.15s,
    visibility 0.15s,
    transform 0.15s;
`;

const ResultLink = styled(Link)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: ${colors.text};
  text-decoration: none;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const ResultMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.text};
  line-height: 1.25;
`;

const ResultSubTitle = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-top: 1px;
  font-weight: 500;
  line-height: 1.25;
`;

const ResultTrailing = styled.span`
  flex-shrink: 0;
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${colors.textMuted};
  white-space: nowrap;
  line-height: 1.25;
`;

const EmptyRow = styled.div`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

export const NavbarStoreSearch = () => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { data: user } = useGetUserQuery();
  const useAlgolia = user ? isAlgoliaSearchRole(user.role) : false;
  const searchKeyQuery = useStoreSearchKeyQuery(user, useAlgolia);
  const searchConfig = searchKeyQuery.data ?? null;

  const resellerId =
    user?.role === UserRole.RESELLER_ADMIN && user.resellerId
      ? user.resellerId
      : undefined;

  const storeSearch = useStoreSearch(searchConfig, {
    resellerId,
    defaultActiveOnly: true,
  });
  const storeSearchRef = useRef(storeSearch);
  useEffect(() => {
    storeSearchRef.current = storeSearch;
  }, [storeSearch]);

  useEffect(() => {
    const trimmed = inputValue.trim();
    const handle = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [inputValue]);

  useEffect(() => {
    if (!searchConfig) {
      return;
    }
    if (debouncedQuery.length < 2) {
      return;
    }
    storeSearchRef.current.search(debouncedQuery);
  }, [debouncedQuery, searchConfig]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const el = wrapperRef.current;
      if (!el || el.contains(event.target as Node)) {
        return;
      }
      setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  if (!useAlgolia || !user) {
    return null;
  }
  if (!searchKeyQuery.isSuccess || !searchConfig) {
    return null;
  }

  const trimmedInput = inputValue.trim();
  const inSyncWithDebounce = trimmedInput === debouncedQuery;
  const showPanel = dropdownOpen && trimmedInput.length >= 2;
  const awaitingDebounce =
    trimmedInput.length >= 2 && !inSyncWithDebounce;
  const canShowHitList =
    inSyncWithDebounce && debouncedQuery.length >= 2 && !storeSearch.isSearching;
  const visibleHits = canShowHitList
    ? storeSearch.hits.slice(0, MAX_DROPDOWN_HITS)
    : [];

  const clearSearch = () => {
    setInputValue("");
    setDebouncedQuery("");
    setDropdownOpen(false);
  };

  return (
    <Wrapper ref={wrapperRef} data-navbar-store-search>
      <SearchInput
        type="search"
        value={inputValue}
        autoComplete="off"
        placeholder={t("navbar.storeSearchPlaceholder")}
        onChange={(e) => {
          setInputValue(e.target.value);
          setDropdownOpen(true);
        }}
        onFocus={() => setDropdownOpen(true)}
      />
      <Dropdown $visible={showPanel}>
        {awaitingDebounce || storeSearch.isSearching ? (
          <EmptyRow>{t("navbar.storeSearchSearching")}</EmptyRow>
        ) : visibleHits.length === 0 ? (
          <EmptyRow>{t("navbar.storeSearchNoResults")}</EmptyRow>
        ) : (
          visibleHits.map((hit) => {
            const display = (hit.displayName ?? "").trim();
            const name = (hit.name ?? "").trim();
            const title = display || name;
            const subTitle =
              display.length > 0 && name.length > 0 && display !== name
                ? name
                : undefined;
            const org = (hit.orgNumber ?? "").trim();
            return (
              <ResultLink
                key={hit.id}
                to={`/stores/${hit.id}/overview`}
                onClick={clearSearch}
              >
                <ResultMain>
                  <ResultTitle>{title}</ResultTitle>
                  {subTitle ? <ResultSubTitle>{subTitle}</ResultSubTitle> : null}
                </ResultMain>
                {org ? <ResultTrailing>{org}</ResultTrailing> : null}
              </ResultLink>
            );
          })
        )}
      </Dropdown>
    </Wrapper>
  );
};
